import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useFacilityToday } from "@/hooks/use-facility-today";
import { toast } from "sonner";
import { codeLabel, displayAmount, errorWords } from "@alm/domain";
import type { MonthlyResponsibilityInput } from "@alm/validation";
import { PageHeader } from "@/components/app/shell/page-header";
import { BilledResident } from "@/components/app/payments/billed-resident";
import { Balance } from "@/components/app/payments/balance";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { ReportExport } from "@/components/app/documents/report-export";
import { ReportTable, type ReportColumn } from "@/components/app/documents/report-table";
import { ResponsibilityFilters } from "@/routes/_authenticated/_financials/monthly-responsibility/-components/responsibility-filters";
import { StatTile } from "@/components/app/lists/stat-tile";
import { StatTiles } from "@/components/app/lists/stat-tiles";
import { OptionSelect, SelectOption } from "@/components/ui/option-select";
import { saveFile } from "@/lib/files/download";
import { useFacility } from "@/lib/facility/facility";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { optionalMonth, stringParams, KEEP_PLACE, optionalSearch } from "@/lib/search";
import { cn } from "@/lib/utils";
import { orpc, orpcClient, type Output } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { keepWhileSame } from "@/lib/query/keep-while-same";

export const Route = createFileRoute("/_authenticated/_financials/monthly-responsibility/")({
  validateSearch: (
    search: Record<string, unknown>,
  ): { q?: string; period?: string; payerType?: string; payer?: string } => ({
    q: optionalSearch(search.q),
    period: optionalMonth(search.period),
    ...stringParams(["payerType", "payer"], search),
  }),
  component: MonthlyResponsibility,
});

type PayerTypeFilter = NonNullable<MonthlyResponsibilityInput["payerType"]>;
type PayerFilter = NonNullable<MonthlyResponsibilityInput["payer"]>;

const payerTypeLabel = (payerType: string | null) => {
  if (payerType === "private_pay") return "Private pay";
  if (payerType === "medicaid") return "Medicaid";
  if (payerType === "va") return "VA";
  return "N/A";
};

const INVOICE_TYPE_LABEL: Record<string, string> = {
  private_pay: "Private pay",
  medicaid_pr: "Medicaid PR",
  medicaid_claim: "Medicaid claim",
};

const paidBy = (payer: string | null) =>
  (payer ?? "self_pay") === "trust_fund" ? "Trust fund" : "Self pay";

type SetupRow = Output<typeof orpcClient.financials.reports.monthlyResponsibility>["setup"][number];

type BilledRow = Output<
  typeof orpcClient.financials.reports.monthlyResponsibility
>["items"][number];

/** Hoisted: the columns close over nothing, and a table is not a component. */
const BILLED_COLUMNS: ReadonlyArray<ReportColumn<BilledRow>> = [
  { header: "Room", cell: (row) => row.roomNumber ?? "-", csvOnly: true },
  {
    header: "Resident",
    cell: (row) => (
      <BilledResident
        name={row.residentName}
        residentId={row.residentId}
        detail={row.roomNumber ? `Room ${row.roomNumber}` : null}
      />
    ),
    value: (row) => row.residentName,
    pin: true,
  },
  { header: "Invoice", cell: (row) => row.invoiceNumber ?? "-" },
  {
    header: "Type",
    priority: 2,
    cell: (row) => INVOICE_TYPE_LABEL[row.invoiceType ?? ""] ?? codeLabel(row.invoiceType),
    value: (row) => row.invoiceType,
  },
  { header: "Billed", cell: (row) => row.totalDue, align: "right" },
  // The API writes a zero discount as "$0"; the other columns say "$0.00".
  { header: "Discount", priority: 3, cell: (row) => displayAmount(row.discount), align: "right" },
  { header: "Paid", cell: (row) => row.paid, align: "right" },
  {
    header: "Balance",
    cell: (row) => <Balance amount={row.balance} pastDue={false} />,
    value: (row) => row.balance,
    align: "right",
  },
];

/** Built outside the component: a table of columns is not itself a component, and the one editable
 * cell needs the mutation handed to it. */
const setupColumns = ({
  canWrite,
  pending,
  onPaidBy,
  showFacility,
}: {
  canWrite: boolean;
  pending: boolean;
  onPaidBy: (residentId: string, payer: "self_pay" | "trust_fund") => void;
  /** Across every building the rows need saying whose they are, as the old sheet did. */
  showFacility: boolean;
}): ReadonlyArray<ReportColumn<SetupRow>> => [
  { header: "Room", cell: (row) => row.roomNumber ?? "-", csvOnly: true },
  {
    header: "Resident",
    cell: (row) => (
      <BilledResident
        name={`${row.lastName}, ${row.firstName}`}
        residentId={row.id}
        detail={row.roomNumber ? `Room ${row.roomNumber}` : null}
      />
    ),
    value: (row) => `${row.lastName}, ${row.firstName}`,
    pin: true,
  },
  { header: "Payer type", priority: 2, cell: (row) => payerTypeLabel(row.payerType) },
  // How they pay, not who pays: a resident on a bank draft and one
  // who brings a cheque are the same payer type and a different job.
  { header: "Payment mode", priority: 3, cell: (row) => row.paymentMode || "-" },
  /* Every amount through the same formatter. Four of these five columns
           printed the stored decimal — "1203.77" and "909" beside a Total
           reading "$2,112.77", in one row of one money table. */
  { header: "Rent", cell: (row) => displayAmount(row.monthlyRent), align: "right" },
  {
    header: "Level of care",
    priority: 2,
    cell: (row) => displayAmount(row.levelOfCareCharge),
    align: "right",
  },
  {
    header: "CBA co-pay",
    priority: 3,
    cell: (row) => displayAmount(row.cbaCoPayAmount),
    align: "right",
  },
  {
    header: "CBA R&B",
    priority: 3,
    cell: (row) => displayAmount(row.cbaRoomAndBoardAmount),
    align: "right",
  },
  { header: "Total", cell: (row) => row.monthlyResponsibility, align: "right" },
  {
    header: "Paid by",
    cell: (row) =>
      canWrite ? (
        <OptionSelect
          className="h-8 w-32"
          aria-label={`Who pays for ${row.lastName}, ${row.firstName}`}
          value={row.patientResponsibilityPayer ?? "self_pay"}
          disabled={pending}
          onChange={(event) => onPaidBy(row.id, event.target.value as "self_pay" | "trust_fund")}
        >
          <SelectOption value="self_pay">Self pay</SelectOption>
          <SelectOption value="trust_fund">Trust fund</SelectOption>
        </OptionSelect>
      ) : (
        paidBy(row.patientResponsibilityPayer)
      ),
    value: (row) => paidBy(row.patientResponsibilityPayer),
  },
  ...(showFacility
    ? [
        {
          header: "Facility",
          priority: 3 as const,
          cell: (row: SetupRow) => row.facilityName ?? "-",
        },
      ]
    : []),
];

/** What each resident is responsible for this month, line by line. */
function MonthlyResponsibility() {
  const { facilityId } = useFacility();
  const today = useFacilityToday(facilityId);
  const canWrite = useCan("resident:update");
  const queryClient = useQueryClient();
  const params = Route.useSearch();
  const { q } = params;
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: { period?: string; payerType?: string; payer?: string }) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const period = params.period ?? today.slice(0, 7);
  const setPeriod = (next: string) =>
    patch({ period: next === today.slice(0, 7) ? undefined : next });
  const payerType = (params.payerType ?? "all") as PayerTypeFilter;
  const setPayerType = (next: PayerTypeFilter) =>
    patch({ payerType: next === "all" ? undefined : next });
  const payer = (params.payer ?? "all") as PayerFilter;
  const setPayer = (next: PayerFilter) => patch({ payer: next === "all" ? undefined : next });
  const {
    text: search,
    change: setSearch,
    settling,
  } = useSearchParamText(
    q,
    (next) =>
      void navigate({
        search: (prev) => ({ ...prev, q: next }),
        ...KEEP_PLACE,
      }),
  );

  const input = {
    facilityId,
    period,
    search: search.trim() || undefined,
    payerType,
    payer,
  };
  const report = useQuery({
    ...orpc.financials.reports.monthlyResponsibility.queryOptions({
      input: { ...input, search: q || undefined },
    }),
    placeholderData: keepWhileSame({ facilityId, period }),
  });
  const searching = settling || report.isPlaceholderData;
  const busy = cn("space-y-3 transition-opacity", searching && "opacity-60");

  /* Who pays the patient responsibility is decided here, on the sheet where
     somebody notices it is wrong, rather than by opening each chart. */
  const savePaidBy = useMutation(
    orpc.care.residents.update.mutationOptions({
      onSuccess: async () => {
        toast.success("Who pays updated");
        // The resident row feeds every screen that names the resident, so every query rereads.
        await queryClient.invalidateQueries();
      },
      onError: (error: Error) => toast.error(errorWords(error)),
    }),
  );

  // The sheet carried into a payer meeting, on the building's letterhead and
  // honouring whatever is filtered on screen.
  const download = useMutation(
    orpc.financials.reports.monthlyResponsibilityExport.mutationOptions({
      onSuccess: saveFile,
      onError: (error: Error) => toast.error(errorWords(error)),
    }),
  );

  return (
    <>
      <PageHeader
        title="Monthly responsibility"
        meta="What each resident is responsible for, and what is still open."
        actions={
          <ReportExport
            formats={["pdf"]}
            pending={download.isPending}
            onExport={() => download.mutate(input)}
          />
        }
      />

      <ResponsibilityFilters
        period={period}
        payerType={payerType}
        payer={payer}
        search={search}
        onPeriod={(next) => setPeriod(next || today.slice(0, 7))}
        onPayerType={setPayerType}
        onPayer={setPayer}
        onSearch={setSearch}
      />

      {report.isError ? (
        <ReadFailed
          message="The responsibility sheet could not be loaded."
          error={report.error}
          onRetry={() => void report.refetch()}
          retrying={report.isFetching}
        />
      ) : (
        <>
          {report.data ? (
            <>
              <StatTiles>
                <StatTile label="Set up to owe" value={report.data.setupTotals.total} />
                <StatTile label="Billed" value={report.data.totals.billed} />
                <StatTile label="Paid" value={report.data.totals.paid} />
                <StatTile label="Outstanding" value={report.data.totals.outstanding} />
              </StatTiles>

              {/* Who is responsible, split by who is behind them. The payer mix is
                  the question this report is run to answer at a payer meeting. */}
              <StatTiles>
                <StatTile label="Private pay" value={report.data.setupTotals.privatePay} />
                <StatTile label="Medicaid" value={report.data.setupTotals.medicaid} />
                <StatTile label="VA" value={report.data.setupTotals.va} />
              </StatTiles>
            </>
          ) : null}

          <section className={busy} aria-busy={searching || undefined}>
            <h2 className="text-lg font-semibold">Standing arrangement</h2>
            <ReportTable
              rows={report.data?.setup}
              pending={report.isPending}
              rowKey={(row) => row.id}
              filename={`monthly-responsibility-${period}.csv`}
              noun="residents"
              empty={{
                title: "Nobody matches",
                description: "Active residents in this building appear here.",
              }}
              columns={setupColumns({
                canWrite,
                showFacility: facilityId === "all",
                pending: savePaidBy.isPending,
                onPaidBy: (residentId, whoPays) =>
                  savePaidBy.mutate({ residentId, patientResponsibilityPayer: whoPays }),
              })}
            />
          </section>

          <section className={busy} aria-busy={searching || undefined}>
            <h2 className="text-lg font-semibold">Billed this month</h2>
            <ReportTable
              rows={report.data?.items}
              pending={report.isPending}
              filename={`monthly-responsibility-invoices-${period}.csv`}
              noun="invoices"
              empty={{
                title: "Nothing billed",
                description: "Invoices for this month appear here.",
              }}
              columns={BILLED_COLUMNS}
            />
          </section>
        </>
      )}
    </>
  );
}
